import {
  getResponsesVisibilityOptions,
  getResponsesVisibilityQueryKey,
  updateResponsesVisibilityMutation,
  useHeyApi,
} from "@platform/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef } from "react";

import {
  deriveResponsesVisibility,
  responsesHiddenSetsEqual,
  toResponsesVisibilityGridState,
} from "./buildResponsesVisibilityGridState";

import type { ResponsesHiddenSet } from "./buildResponsesVisibilityGridState";
import type { ResponsesGridRow } from "./types";
import type { MpxAgGridStateProps } from "@platform/hooks";
import type { GridState, StateUpdatedEvent } from "ag-grid-community";

const PERSIST_DEBOUNCE_MS = 500;

interface UseResponsesVisibilityGridStateConfig {
  rfpId: string;
  /** Row data, used to map hidden carrier ids to the carrier set filter. */
  rows: ResponsesGridRow[];
  /** The mpx-backed grid props (order/width/sort) to layer visibility onto. */
  gridProps: MpxAgGridStateProps;
  enabled?: boolean;
}

interface UseResponsesVisibilityGridStateResult {
  gridProps: MpxAgGridStateProps;
  isLoading: boolean;
}

/**
 * Persists the Responses grid's hidden carriers and coverages to the shared,
 * per-RFP responses-visibility store, replacing the per-user localStorage view
 * so every analyst on the RFP — and the Marketing Presentation export — sees
 * the same hidden set. Seeds the grid's column-visibility and carrier-filter
 * facets from the store and pushes changes back on state updates.
 */
export const useResponsesVisibilityGridState = ({
  rfpId,
  rows,
  gridProps,
  enabled = true,
}: UseResponsesVisibilityGridStateConfig): UseResponsesVisibilityGridStateResult => {
  const { centroPortalClient } = useHeyApi();
  const queryClient = useQueryClient();

  const queryKey = getResponsesVisibilityQueryKey({
    path: { id: rfpId },
    client: centroPortalClient,
  });

  const { data, isLoading } = useQuery({
    ...getResponsesVisibilityOptions({
      path: { id: rfpId },
      client: centroPortalClient,
    }),
    enabled: enabled && !!rfpId,
    staleTime: 5 * 60 * 1000,
  });

  const hiddenSet = useMemo<ResponsesHiddenSet>(
    () => ({
      hiddenCarrierIds: data?.hiddenCarrierIds ?? [],
      hiddenPlanGroupIds: data?.hiddenPlanGroupIds ?? [],
    }),
    [data],
  );

  // Tracks the hidden set of the currently scheduled/in-flight write (if any),
  // kept distinct from server truth so a pending or failed write is never
  // mistaken for persisted state.
  const pendingRef = useRef<ResponsesHiddenSet | undefined>(undefined);

  const { mutate } = useMutation({
    ...updateResponsesVisibilityMutation({ client: centroPortalClient }),
    onSuccess: (response) => {
      queryClient.setQueryData(queryKey, response);
    },
    onSettled: () => {
      // Clear the in-flight target once the request finishes: on success the
      // query effect adopts the confirmed state as truth; on failure this frees
      // a later state event to re-attempt the write.
      pendingRef.current = undefined;
    },
  });

  const {
    initialState: serverInitialState,
    onStateUpdated: serverOnStateUpdated,
  } = gridProps;

  const initialState = useMemo<GridState | undefined>(() => {
    const { columnVisibility, filter } = toResponsesVisibilityGridState(
      hiddenSet,
      rows,
    );

    if (!serverInitialState && !columnVisibility && !filter) {
      return undefined;
    }

    return { ...serverInitialState, columnVisibility, filter };
  }, [serverInitialState, hiddenSet, rows]);

  // Tracks the hidden set already confirmed server-side so echoes of the
  // seeded state (and no-op state updates) never trigger a redundant PUT.
  // Kept in sync with server truth whenever the query resolves.
  const lastPersistedRef = useRef<ResponsesHiddenSet>(hiddenSet);
  useEffect(() => {
    lastPersistedRef.current = hiddenSet;
  }, [hiddenSet]);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  const onStateUpdated = useCallback(
    (event: StateUpdatedEvent) => {
      serverOnStateUpdated(event);

      if (!enabled) {
        return;
      }

      if (event.sources?.includes("gridInitializing")) {
        return;
      }

      const next = deriveResponsesVisibility(event.api);

      // Reverted to server truth: cancel any scheduled write, nothing to persist.
      if (responsesHiddenSetsEqual(next, lastPersistedRef.current)) {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = undefined;
        pendingRef.current = undefined;
        return;
      }

      // Already scheduled/sent this exact state; don't stack a duplicate write.
      if (
        pendingRef.current &&
        responsesHiddenSetsEqual(next, pendingRef.current)
      ) {
        return;
      }

      pendingRef.current = next;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        mutate({
          path: { id: rfpId },
          body: {
            hiddenCarrierIds: next.hiddenCarrierIds,
            hiddenPlanGroupIds: next.hiddenPlanGroupIds,
          },
        });
      }, PERSIST_DEBOUNCE_MS);
    },
    [serverOnStateUpdated, mutate, rfpId, enabled],
  );

  return {
    gridProps: { key: gridProps.key, initialState, onStateUpdated },
    isLoading,
  };
};
